import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dropdown, MenuItem } from '@bingads-webui-react/primitive';
import { SearchBox } from '@fluentui/react';
import { BingMapsContext } from '../bing-maps-context/index';
import { useBingMapsSearch } from '../use-bing-maps-search/index';
import {
  getIsBoundaryTarget as defaultGetIsBoundaryTarget,
  getIsRadiusTarget as defaultGetIsRadiusTarget,
  isLocationEqual,
  getLocationName,
  LocationTarget
} from '../util/index';
import { getStyles } from './get-styles';
import { TARGETING_TYPE } from '../util/targeting-type';
import { SearchResult } from './search-result';
import { CustomDropdownToggle } from './custom-dropdown-toggle';

/**
 * Displays a search box for submitting a searching for locations and a dropdown of search results
 * to create positive or negative location targets
 * @param {*} props - React props.
 * @returns {ReactNode} - Search box UI.
 */
function BingMapsSearchBoxUnstyled(props) {
  const {
    allowPositiveLocationTargeting,
    allowNegativeLocationTargeting,
    classes,
    getIsSearchResultBoundaryTarget,
    getIsSearchResultRadiusTarget,
    id,
    locationTargets,
    isIncludeEnabled,
    isExcludeEnabled,
    isRadiusExcludeEnabled,
    negativeLocationTargets,
    changeDebounceWait,
    noResultsMessage,
    searchResultsFooter,
    searchResultsFooterOnClick,
    onLocationTargetsChange,
    onNegativeLocationTargetsChange,
    placeholder,
    radiusSize,
    radiusUnit,
    isSingleLocationSelect,
    ariaLabel,
    customizedLocationEqual,
    userRegion,
    locale,
    showExcludeButtonIgnoreLocationId,
  } = props;

  const {
    bingMapsApiKey,
    campaignApiServiceRoot,
    campaignApiToken,
    searchOptions,
    logActivity,
    instrumentationService,
  } = useContext(BingMapsContext);

  const searchOptionsWithInstrumentation = useMemo(() => ({
    ...searchOptions,
    instrumentationService,
  }), [searchOptions, instrumentationService]);

  const [{ data, isLoading, hasFilteredOutResults }, { search }] =
    useBingMapsSearch(
      campaignApiServiceRoot,
      campaignApiToken,
      bingMapsApiKey,
      searchOptionsWithInstrumentation,
      userRegion,
      locale
    );
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [hideResultsTimeout, setHideResultsTimeout] = useState(false);
  const [searchboxValue, setSearchboxValue] = useState('');

  const closeSearchResultDropdown = useCallback(() => {
    setIsResultsOpen(false);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(locationTargets) && isSingleLocationSelect) {
      const locationName = getLocationName({
        location: locationTargets[0].location,
        isRadiusTarget: locationTargets[0].isRadiusLocationTarget,
        instrumentationService,
      });
      setSearchboxValue(locationName);
    }
  }, [instrumentationService, isSingleLocationSelect, locationTargets]);

  const debouncedSearch = useMemo(
    () => _.debounce((query) => {
      logActivity(`location search search for location target: ${query}`);
      if (!_.isEmpty(query) && (/\w/.test(query) || /\p{Script=Han}/u.test(query))) { // ensure there's at least one alphanumeric char or Chinese char
        search(query)
          .then(() => {
            setIsResultsOpen(true);
          })
          .catch((err) => {
            logActivity(`location search search for location target failed: ${err instanceof Error ? err : JSON.stringify(err)}`);
          });
      } else {
        // query is empty or no alphanumeric chars, hide the results
        closeSearchResultDropdown();
      }
    }, changeDebounceWait),
    [changeDebounceWait, closeSearchResultDropdown, logActivity, search]
  );


  const handleQueryChange = useCallback((e, value) => {
    debouncedSearch(value);
    if (value) {
      setSearchboxValue(value);
    } else {
      setSearchboxValue('');
    }
  }, [debouncedSearch]);

  const handleBlur = useCallback(() => {
    // use setTimeout to wait for all focus events to be settled before hiding the search results
    const timeoutId = setTimeout(() => { closeSearchResultDropdown(); });
    setHideResultsTimeout(timeoutId);
  }, [closeSearchResultDropdown]);

  const handleFocus = useCallback(() => {
    // if focus has landed on a different part of this search box, don't hide the results
    if (_.isFinite(hideResultsTimeout)) {
      clearTimeout(hideResultsTimeout);
      setHideResultsTimeout(null);
    }
  }, [hideResultsTimeout]);

  // create a new target based on the selected location data
  const createLocationTarget = useCallback((location, targetingType) => {
    if (getIsSearchResultRadiusTarget(location, targetingType)) {
      // create a new radius location target
      return new LocationTarget(location, targetingType, radiusSize, radiusUnit);
    }

    // create a new boundary location target
    return new LocationTarget(location, targetingType);
  }, [getIsSearchResultRadiusTarget, radiusSize, radiusUnit]);

  const handleLocationTargetInclude = useCallback((location) => {
    if (isIncludeEnabled) {
      if (!isSingleLocationSelect) {
        onLocationTargetsChange([
          createLocationTarget(location, TARGETING_TYPE.Included),
          ...locationTargets,
        ]);
        setSearchboxValue('');
      } else {
        onLocationTargetsChange([
          createLocationTarget(location, TARGETING_TYPE.Included),
        ]);
        logActivity('location search add location target');
      }
    }
    closeSearchResultDropdown();
  }, [isIncludeEnabled, closeSearchResultDropdown, isSingleLocationSelect, onLocationTargetsChange,
    createLocationTarget, locationTargets, logActivity]);

  const handleLocationTargetExclude = useCallback((location) => {
    onNegativeLocationTargetsChange([
      createLocationTarget(location, TARGETING_TYPE.Excluded),
      ...negativeLocationTargets,
    ]);
    closeSearchResultDropdown();
    setSearchboxValue('');
  }, [onNegativeLocationTargetsChange, createLocationTarget, negativeLocationTargets,
    closeSearchResultDropdown]);

  const handleMenuItemSelect = (location) => {
    if (!allowNegativeLocationTargeting && allowPositiveLocationTargeting) {
      // if including is the only action permitted, allow the MenuItem to function as target button
      handleLocationTargetInclude(location);
    }
  };

  const isExistingTarget = useCallback((location, existingTargets, isRadiusTarget) => _.find(
    existingTargets,
    (existingTarget) => {
      if (_.isFunction(customizedLocationEqual)) {
        return customizedLocationEqual(
          existingTarget,
          {
            location, isRadiusLocationTarget: isRadiusTarget, radiusSize, radiusUnit,
          }
        );
      }
      if (existingTarget.isRadiusLocationTarget !== isRadiusTarget) {
        return false;
      }
      const areRadiusLocations = existingTarget.isRadiusLocationTarget && isRadiusTarget;
      return isLocationEqual(existingTarget.location, location, areRadiusLocations);
    }
  ), [customizedLocationEqual, radiusSize, radiusUnit]);

  const searchResults = useMemo(() => {
    if (data == null) {
      // allow searchResults to be null or undefined, _.map will return an empty array
      return data;
    }

    return _.chain(data)
      .map((location) => {
        // prefer to target locations by radius
        const isRadiusTarget = getIsSearchResultRadiusTarget(location);

        if (!isRadiusTarget && !getIsSearchResultBoundaryTarget(location)) {
          return null;
        }

        return {
          location,
          isRadiusTarget,
        };
      })
      .compact()
      .value();
  }, [data, getIsSearchResultRadiusTarget, getIsSearchResultBoundaryTarget]);

  useEffect(() => {
    if (data !== null &&
      _.isEmpty(searchResults) &&
      !isLoading) {
      instrumentationService.logTrace(`Searched address not in target country. Address searched: ${searchboxValue}`);
    }
  }, [data, instrumentationService, isLoading, searchResults, searchboxValue]);

  useEffect(() => {
    if (searchResults != null) {
      if (isResultsOpen) {
        instrumentationService.logTrace(`Bing map search results dropdown open: ${_.size(searchResults)} result(s).`);
      } else {
        instrumentationService.logTrace('Bing map search results dropdown closed.');
      }
    }
  }, [searchResults, isResultsOpen, instrumentationService]);

  const onFocusShowDropdown = useCallback(() => {
    if (!_.isEmpty(searchboxValue) && /\w/.test(searchboxValue)) {
      setIsResultsOpen(true);
      if (_.isEmpty(searchResults)) {
        debouncedSearch(searchboxValue);
      }
    }
  }, [debouncedSearch, searchResults, searchboxValue]);

  const searchResultsFooterContent = useMemo(() => {
    if (_.isFunction(searchResultsFooter)) {
      const content = searchResultsFooter(searchResults, hasFilteredOutResults, searchboxValue);

      if (content) {
        setIsResultsOpen(true);
        return (
          <MenuItem
            onSelect={searchResultsFooterOnClick}
          >
            <div className="bing-maps-search-box-search-result bing-maps-search-box-search-results-footer">
              <div className="bing-maps-search-box-search-result-name">
                {content}
              </div>
            </div>
          </MenuItem>
        );
      }
    }
    return null;
  }, [searchResultsFooter,
    searchResults,
    hasFilteredOutResults,
    searchboxValue,
    searchResultsFooterOnClick]);

  return (
    <div
      className={`bing-maps-search-box-container ${classes.searchBoxContainer}`}
      onBlur={handleBlur}
      onFocus={handleFocus}
    >
      <Dropdown
        id="bing-maps-search-box-dropdown"
        className="bing-maps-search-box-dropdown"
        open={isResultsOpen}
        onToggle={_.noop}
      >
        <CustomDropdownToggle bsRole="toggle">
          <SearchBox
            autoComplete="off"
            className="bing-maps-search-box-input"
            id={id}
            placeholder={placeholder}
            onChange={handleQueryChange}
            onSearch={debouncedSearch}
            value={searchboxValue}
            onFocus={onFocusShowDropdown}
            ariaLabel={ariaLabel}
            maxLength="200"
            data-clarity-unmask="true"
            instrumentationService={instrumentationService}
          />
        </CustomDropdownToggle>
        <Dropdown.Menu
          role="menu"
          className="bing-maps-search-box-results-menu"
          id="bing-maps-search-box-results-menu"
          data-clarity-unmask="true"
        >
          <div role="status" >
            {_.isEmpty(searchResults) && noResultsMessage &&
              <MenuItem disabled>
                <div className="bing-maps-search-box-search-result bing-maps-search-box-search-no-result">
                  <div className="bing-maps-search-box-search-result-name">
                    {noResultsMessage}
                  </div>
                </div>
              </MenuItem>
            }
            {_.map(searchResults, ({ location, isRadiusTarget }, i) => {
              let targetingType = TARGETING_TYPE.New;
              if (isExistingTarget(location, locationTargets, isRadiusTarget)) {
                targetingType = TARGETING_TYPE.Included;
              } else if (isExistingTarget(location, negativeLocationTargets, isRadiusTarget)) {
                targetingType = TARGETING_TYPE.Excluded;
              }

              return (
                <MenuItem
                  // searchboxValue added below to update element every time search query changes
                  // so that the screen reader reads all suggestions
                  key={`${searchboxValue}-${location.Id || i}`}
                  disabled={targetingType !== TARGETING_TYPE.New}
                  onSelect={() => handleMenuItemSelect(location)}
                >
                  <SearchResult
                    isAlreadyTargeted={targetingType !== TARGETING_TYPE.New}
                    isRadiusTarget={isRadiusTarget}
                    isIncludeEnabled={isIncludeEnabled}
                    isExcludeEnabled={isExcludeEnabled}
                    isRadiusExcludeEnabled={isRadiusExcludeEnabled}
                    location={location}
                    onLocationTargetInclude={handleLocationTargetInclude}
                    onLocationTargetExclude={handleLocationTargetExclude}
                    radiusUnit={radiusUnit}
                    radiusValue={radiusSize}
                    showExcludeButton={allowNegativeLocationTargeting}
                    showIncludeButton={allowPositiveLocationTargeting}
                    targetingType={targetingType}
                    closeSearchResultDropdown={closeSearchResultDropdown}
                    showExcludeButtonIgnoreLocationId={showExcludeButtonIgnoreLocationId}
                  />
                </MenuItem>
              );
            })}
            {searchResultsFooterContent}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

BingMapsSearchBoxUnstyled.propTypes = {
  /**
   * True if user is allowed to add to a list of included locations
   */
  allowPositiveLocationTargeting: PropTypes.bool,
  /**
   * True if user is allowed to add to a list of excluded locations
   */
  allowNegativeLocationTargeting: PropTypes.bool,
  /**
   * Amount of time in ms to wait before initiating search requests.
   */
  changeDebounceWait: PropTypes.number.isRequired,
  /**
   * CSS classes.
   */
  classes: PropTypes.shape({ searchBoxContainer: PropTypes.string }).isRequired,
  /**
   * Function that receives a search result and determines if it is or isn't available for boundary
   * targeting.
   */
  getIsSearchResultBoundaryTarget: PropTypes.func,
  customizedLocationEqual: PropTypes.func,
  /**
   * Function that receives a search result and determines if it is or isn't available for radius
   * targeting.
   */
  getIsSearchResultRadiusTarget: PropTypes.func,
  id: PropTypes.string,
  /**
   * List of locations currently being targeted.
   */
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,
  /**
   * True if user can add a new location target from the search box.
   */
  isIncludeEnabled: PropTypes.bool,
  /**
   * True if user can add a new negative location target from the search box.
   */
  isExcludeEnabled: PropTypes.bool,
  /**
   * True if user can add a new negative location target of radius type from the search box.
   */
  isRadiusExcludeEnabled: PropTypes.bool,
  /**
   * List of locations currently being excluded.
   */
  negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  noResultsMessage: PropTypes.string,
  searchResultsFooter: PropTypes.func,
  searchResultsFooterOnClick: PropTypes.func,
  /**
   * Callback for when location targets have changed.
   */
  onLocationTargetsChange: PropTypes.func.isRequired,
  /**
   * Callback for when negative location targets have changed.
   */
  onNegativeLocationTargetsChange: PropTypes.func,
  placeholder: PropTypes.string,
  /**
   * Unit of distance to be used for radius targeting, e.g. miles.
   */
  radiusUnit: PropTypes.string,
  /**
   * Distance to be used for radius targeting.
   */
  radiusSize: PropTypes.number,
  /**
   * True if there can only be one location
   * only supported if isExcludeEnabled is false
   */
  isSingleLocationSelect: PropTypes.bool,
  ariaLabel: PropTypes.string,
  userRegion: PropTypes.string,
  locale: PropTypes.string,
  showExcludeButtonIgnoreLocationId: PropTypes.bool,
};

BingMapsSearchBoxUnstyled.defaultProps = {
  allowPositiveLocationTargeting: true,
  allowNegativeLocationTargeting: false,
  noResultsMessage: null,
  searchResultsFooter: null,
  customizedLocationEqual: null,
  searchResultsFooterOnClick: _.noop,
  getIsSearchResultBoundaryTarget: defaultGetIsBoundaryTarget,
  getIsSearchResultRadiusTarget: defaultGetIsRadiusTarget,
  id: null,
  isIncludeEnabled: true,
  isExcludeEnabled: true,
  negativeLocationTargets: null,
  onNegativeLocationTargetsChange: null,
  placeholder: null,
  radiusUnit: 'mi',
  radiusSize: 25,
  isSingleLocationSelect: false,
  ariaLabel: null,
  isRadiusExcludeEnabled: true,
  userRegion: undefined,
  locale: undefined,
  showExcludeButtonIgnoreLocationId: false,
};

export const BingMapsSearchBox = withDefaultStyles(BingMapsSearchBoxUnstyled, getStyles);
